/**
 * Product Hierarchy — Settings surface for the product tree (hierarchy type
 * `product`, migration 719).
 *
 * Portfolio → Product line → Product → Component, per workspace. Sits beside
 * the org chart as a second first-class hierarchy type and is deliberately
 * NOT an item-relationship surface: placing an item here carries no gate
 * weight (canon 12 §5.8).
 *
 * Route:  /prioritization/settings/product-hierarchy
 * Gate:   product_hierarchy module × PRODUCT_HIERARCHY flag (useModuleGate)
 * Spec:   docs/9-exploration/HIERARCHY-TYPES-ARCHITECTURE-BRIEF.md §8 Phase 2
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Archive,
  Check,
  Layers,
  Loader2,
  Package,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { Modal, Select } from '../../../components/ds';
import { Button } from '../../../components/ui/Button';
import { HierarchyTreePicker } from '../../../components/hierarchy/HierarchyTreePicker';
import { StructureHistoryPanel } from '../../../components/hierarchy/StructureHistoryPanel';
import { StructureReorgWorkspace } from '../../../components/hierarchy/StructureReorgWorkspace';
import { useStructureReorgEnabled } from '../../../features/hierarchy/hooks/useStructureReorgEnabled';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { isAdminRole } from '../../../types/prioritization/roles';
import {
  PRODUCT_NODE_KINDS,
  PRODUCT_NODE_KIND_LABELS,
  defaultChildProductKind,
  formatHierarchyPathLabel,
  hierarchyDescendantIds,
  productNodeToHierarchyNode,
  type HierarchyNode,
  type ProductNode,
  type ProductNodeKind,
} from '../../../lib/hierarchy/hierarchyTypes';
import {
  countItemsByProductNode,
  createProductNode,
  retireProductNode,
  restoreProductNode,
  listProductNodes,
  updateProductNode,
} from '../../../lib/hierarchy/productNodesService';

// ── Form model ────────────────────────────────────────────────────────────────

interface NodeForm {
  name: string;
  kind: ProductNodeKind;
  parentId: string | null;
  description: string;
  externalId: string;
}

const EMPTY_FORM: NodeForm = {
  name: '',
  kind: 'portfolio',
  parentId: null,
  description: '',
  externalId: '',
};

function KindBadge({ kind }: { kind: ProductNodeKind }) {
  return (
    <span className="inline-block font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--hover-tint)] text-[var(--text-ter)]">
      {PRODUCT_NODE_KIND_LABELS[kind]}
    </span>
  );
}

const inputCls =
  'w-full text-sm border border-[var(--rule)] rounded px-2 py-1.5 bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)]';

// ── Modal ─────────────────────────────────────────────────────────────────────

function NodeModal({
  title,
  form,
  setForm,
  nodes,
  editingId,
  saving,
  error,
  onSave,
  onCancel,
}: {
  title: string;
  form: NodeForm;
  setForm: (f: NodeForm) => void;
  nodes: HierarchyNode[];
  editingId: string | null;
  saving: boolean;
  error: string | null;
  onSave: () => void;
  onCancel: () => void;
}) {
  // While editing, a node cannot become its own descendant's child.
  const disabledIds = useMemo(() => {
    if (!editingId) return undefined;
    const set = new Set(hierarchyDescendantIds(editingId, nodes));
    set.add(editingId);
    return set;
  }, [editingId, nodes]);

  return (
    <Modal
      open
      onClose={onCancel}
      title={title}
      label="Product hierarchy"
      size="md"
      footer={
        <>
          <Button
            onClick={onCancel}
            variant="secondary" size="sm" className="px-3 py-2 text-sm"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !form.name.trim()}
            variant="primary" className="px-4 py-2 disabled:opacity-50 text-sm"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">
            Name <span className="text-[var(--coral)]">*</span>
          </label>
          <input
            className={inputCls}
            value={form.name}
            placeholder="e.g. Payments, Mobile app, Checkout"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            autoFocus
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Level</label>
            <Select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value as ProductNodeKind })}
              options={PRODUCT_NODE_KINDS.map((k) => ({ value: k, label: PRODUCT_NODE_KIND_LABELS[k] }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">External id</label>
            <input
              className={inputCls}
              value={form.externalId}
              placeholder="Catalog / PLM / Jira key"
              onChange={(e) => setForm({ ...form, externalId: e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Parent</label>
          <HierarchyTreePicker
            nodes={nodes}
            selectedId={form.parentId}
            onSelect={(id) => {
              const parent = id ? nodes.find((n) => n.id === id) : null;
              setForm({
                ...form,
                parentId: id,
                kind: editingId ? form.kind : defaultChildProductKind((parent?.levelKey as ProductNodeKind | undefined) ?? null),
              });
            }}
            disabledIds={disabledIds}
            noneLabel="— top level (no parent) —"
            renderMeta={(n) => <KindBadge kind={n.levelKey as ProductNodeKind} />}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Description</label>
          <textarea
            rows={2}
            className={inputCls}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        {error && <p className="text-xs text-[var(--coral)]">{error}</p>}
      </div>
    </Modal>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ProductHierarchy() {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const structureReorgEnabled = useStructureReorgEnabled();
  const currentRole = useUserStore((s) => s.currentRole);
  const addNotification = useUIStore((s) => s.addNotification);
  const canEdit = currentRole ? isAdminRole(currentRole) : false;

  const [rows, setRows] = useState<ProductNode[]>([]);
  const [itemCounts, setItemCounts] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [modal, setModal] = useState<{ mode: 'create' | 'edit'; id: string | null } | null>(null);
  const [form, setForm] = useState<NodeForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const nodes = useMemo(() => rows.map(productNodeToHierarchyNode), [rows]);
  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);
  const selected = selectedId ? byId.get(selectedId) ?? null : null;

  const load = useCallback(async () => {
    if (!workspaceId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const [list, counts] = await Promise.all([
        listProductNodes(workspaceId, { includeInactive: true }),
        countItemsByProductNode(workspaceId).catch(() => new Map<string, number>()),
      ]);
      setRows(list);
      setItemCounts(counts);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = (parentId: string | null) => {
    const parent = parentId ? byId.get(parentId) ?? null : null;
    setForm({
      ...EMPTY_FORM,
      parentId,
      kind: defaultChildProductKind(parent?.kind ?? null),
    });
    setFormError(null);
    setModal({ mode: 'create', id: null });
  };

  const openEdit = (node: ProductNode) => {
    setForm({
      name: node.name,
      kind: node.kind,
      parentId: node.parentId,
      description: node.description ?? '',
      externalId: node.externalId ?? '',
    });
    setFormError(null);
    setModal({ mode: 'edit', id: node.id });
  };

  const handleSave = async () => {
    if (!workspaceId || !modal) return;
    setSaving(true);
    setFormError(null);
    try {
      if (modal.mode === 'create') {
        const created = await createProductNode({
          workspaceId,
          name: form.name,
          kind: form.kind,
          parentId: form.parentId,
          description: form.description,
          externalId: form.externalId,
        });
        setSelectedId(created.id);
        addNotification({ type: 'success', title: 'Product hierarchy', message: `Added "${created.name}".`, duration: 4000 });
      } else if (modal.id) {
        await updateProductNode(modal.id, {
          name: form.name,
          kind: form.kind,
          parentId: form.parentId,
          description: form.description,
          externalId: form.externalId,
        });
        addNotification({ type: 'success', title: 'Product hierarchy', message: 'Product node updated.', duration: 4000 });
      }
      setModal(null);
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  // Structure nodes are retired, never deleted: the node keeps its id, its
  // name and every item placed on it, so history stays readable (mig 727).
  const handleRetire = async (node: ProductNode) => {
    const placed = itemCounts.get(node.id) ?? 0;
    const parts = [`Retire "${node.name}"? It leaves pickers but stays on the record, and can be restored.`];
    if (placed > 0) parts.push(`\n\n${placed} item${placed === 1 ? '' : 's'} placed here keep${placed === 1 ? 's' : ''} this placement.`);
    if (!confirm(parts.join(' '))) return;
    setDeletingId(node.id);
    try {
      await retireProductNode(node.id);
      addNotification({ type: 'success', title: 'Product hierarchy', message: `Retired "${node.name}".`, duration: 4000 });
      await load();
    } catch (err) {
      addNotification({ type: 'error', title: 'Product hierarchy', message: err instanceof Error ? err.message : String(err), duration: 6000 });
    } finally {
      setDeletingId(null);
    }
  };

  const handleRestore = async (node: ProductNode) => {
    setDeletingId(node.id);
    try {
      await restoreProductNode(node.id);
      addNotification({ type: 'success', title: 'Product hierarchy', message: `Restored "${node.name}".`, duration: 4000 });
      await load();
    } catch (err) {
      addNotification({ type: 'error', title: 'Product hierarchy', message: err instanceof Error ? err.message : String(err), duration: 6000 });
    } finally {
      setDeletingId(null);
    }
  };

  const activeCount = rows.filter((r) => r.active).length;

  return (
    <div className="p-4 md:p-6 max-w-6xl">
      <PageHeader
        icon={<Layers className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Product Hierarchy"
        subtitle={
          loading
            ? 'Loading…'
            : `${activeCount} node${activeCount === 1 ? '' : 's'} · portfolio → product line → product → component`
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              onClick={() => void load()}
              variant="secondary" size="sm" className="px-3 py-2 text-sm"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {canEdit && (
              <Button
                onClick={() => openCreate(null)}
                variant="primary" className="px-3 py-2 text-sm"
              >
                <Plus className="h-3.5 w-3.5" />
                Add top-level node
              </Button>
            )}
          </div>
        }
      />

      <p className="text-sm text-[var(--text-sec)] mb-4 max-w-3xl">
        A second hierarchy type beside the org chart. Placing an item in the product tree
        says <em>where it lives</em> — it never changes how it ranks and is not an item
        relationship. For work-to-work structure (an epic and its stories) use item
        relationships instead.
      </p>

      {loadError && (
        <div className="mb-4 border border-[var(--coral)] bg-[var(--coral-dim)] px-4 py-3 text-sm text-[var(--coral)]">
          {loadError}
        </div>
      )}

      {!loading && rows.length === 0 && !loadError ? (
        <div className="border border-dashed border-[var(--rule)] p-10 text-center">
          <Package className="h-8 w-8 mx-auto text-[var(--text-ter)] mb-3" />
          <h3 className="text-sm font-semibold text-[var(--text-pri)] mb-1">No product hierarchy yet</h3>
          <p className="text-sm text-[var(--text-sec)] mb-4">
            Start with a portfolio or a product line, then add products and components beneath it.
          </p>
          {canEdit ? (
            <Button
              onClick={() => openCreate(null)}
              variant="primary" className="px-3 py-2 text-sm"
            >
              <Plus className="h-3.5 w-3.5" />
              Add the first node
            </Button>
          ) : (
            <p className="text-xs text-[var(--text-ter)]">A workspace admin can add nodes.</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-4">
          <div>
            <HierarchyTreePicker
              nodes={nodes}
              selectedId={selectedId}
              onSelect={setSelectedId}
              className="min-h-[16rem]"
              renderMeta={(n) => {
                const count = itemCounts.get(n.id) ?? 0;
                return (
                  <span className="inline-flex items-center gap-1.5">
                    {!n.active && (
                      <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--text-dim)]">inactive</span>
                    )}
                    {count > 0 && (
                      <span className="font-mono text-[10px] text-[var(--text-ter)]" title={`${count} item(s) placed here`}>
                        {count}
                      </span>
                    )}
                    <KindBadge kind={n.levelKey as ProductNodeKind} />
                  </span>
                );
              }}
            />
          </div>

          <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4 text-sm min-h-[16rem]">
            {!selected ? (
              <p className="text-[var(--text-ter)] text-sm">Select a node to see its details.</p>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-[var(--text-pri)] truncate">{selected.name}</h3>
                    <p className="text-xs text-[var(--text-ter)] mt-0.5 truncate">
                      {formatHierarchyPathLabel(selected.id, nodes)}
                    </p>
                  </div>
                  <KindBadge kind={selected.kind} />
                </div>
                <dl className="grid grid-cols-2 gap-3">
                  <div>
                    <dt className="text-xs text-[var(--text-ter)]">Depth</dt>
                    <dd>{selected.depth}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-[var(--text-ter)]">Items placed here</dt>
                    <dd>{itemCounts.get(selected.id) ?? 0}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-[var(--text-ter)]">External id</dt>
                    <dd className="font-mono text-xs">{selected.externalId ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-[var(--text-ter)]">Status</dt>
                    <dd>{selected.active ? 'Active' : 'Inactive'}</dd>
                  </div>
                  {selected.description && (
                    <div className="col-span-2">
                      <dt className="text-xs text-[var(--text-ter)]">Description</dt>
                      <dd className="text-[var(--text-sec)] mt-1">{selected.description}</dd>
                    </div>
                  )}
                </dl>
                {canEdit && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      onClick={() => openCreate(selected.id)}
                      variant="secondary" size="sm" className="gap-1 px-3 py-1.5 text-xs"
                    >
                      <Plus className="h-3 w-3" /> Add child
                    </Button>
                    <Button
                      onClick={() => openEdit(selected)}
                      variant="secondary" size="sm" className="gap-1 px-3 py-1.5 text-xs"
                    >
                      <Pencil className="h-3 w-3" /> Edit / move
                    </Button>
                    {selected.active ? (
                      <Button
                        disabled={deletingId === selected.id}
                        onClick={() => void handleRetire(selected)}
                        variant="ghost" size="sm" className="inline-flex items-center !gap-1 !px-3 !py-1.5 !text-xs border border-[var(--coral)] text-[var(--coral)] hover:bg-[var(--coral-dim)] disabled:opacity-50"
                      >
                        {deletingId === selected.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Archive className="h-3 w-3" />}
                        Retire
                      </Button>
                    ) : (
                      <Button
                        disabled={deletingId === selected.id}
                        onClick={() => void handleRestore(selected)}
                        variant="secondary" size="sm" className="gap-1 px-3 py-1.5 text-xs"
                      >
                        {deletingId === selected.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                        Restore
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {modal && (
        <NodeModal
          title={modal.mode === 'create' ? 'Add product node' : 'Edit product node'}
          form={form}
          setForm={setForm}
          nodes={nodes}
          editingId={modal.id}
          saving={saving}
          error={formError}
          onSave={() => void handleSave()}
          onCancel={() => setModal(null)}
        />
      )}
      {structureReorgEnabled && canEdit && (
        <div className="mt-6">
          <StructureReorgWorkspace workspaceId={workspaceId} kind="product" onLiveStructureChanged={() => void load()} />
        </div>
      )}
      <div className="mt-6">
        <StructureHistoryPanel
          workspaceId={workspaceId}
          kind="product"
          onReverted={() => void load()}
          refreshKey={rows.map((r) => `${r.id}:${r.parentId ?? ''}:${r.name}:${r.active ? 1 : 0}`).sort().join('|')}
        />
      </div>
    </div>
  );
}
